/**
 * Tracking utilities: context enrichment, GraphQL tracking, and React hooks
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import store from "src/page/store";
import { trackEvent } from "./client";
import { NAVIGATION_EVENTS, GRAPHQL_EVENTS } from "./events";
import type { BaseEventProperties, GraphQLEventProperties } from "./events";

// Context Enrichment (internal functions)
function getPageContext(): Pick<BaseEventProperties, "page_path" | "page_title" | "referrer"> {
  return {
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer
  };
}

function getUserContext(): Pick<BaseEventProperties, "user_id" | "user_role" | "organisation_id"> {
  try {
    const {
      auth: { userInfo, cognitoConfig }
    } = store.getState();

    return {
      user_id: userInfo?.id,
      user_role: userInfo?.role,
      organisation_id: cognitoConfig?.currentOrganisation?.organisation_id
    };
  } catch (error) {
    console.error("Failed to get user context:", error);
    return {};
  }
}

export function enrichEventProperties<T extends Record<string, any>>(properties?: T): T & BaseEventProperties {
  const pageContext = getPageContext();
  const userContext = getUserContext();

  return {
    ...pageContext,
    ...userContext,
    ...(properties || {})
  } as T & BaseEventProperties;
}

// GraphQL Tracking
export function parseGraphQLOperation(query: string): {
  methodName: string;
  methodType: "query" | "mutation";
} {
  const fieldMatch = query.match(/(?:query|mutation)\s+\w+[^{]*\{[\s\n]*([a-zA-Z_][a-zA-Z0-9_]*)/);
  const methodType = query.includes("mutation") ? "mutation" : "query";

  if (fieldMatch && fieldMatch[1]) {
    return { methodName: fieldMatch[1], methodType };
  }

  const operationMatch = query.match(/(?:query|mutation)\s+(\w+)/);

  if (operationMatch) {
    return { methodName: operationMatch[1], methodType };
  }

  return { methodName: "unknown", methodType: "query" };
}

export interface TrackGraphQLOperationParams {
  methodName: string;
  methodType: "query" | "mutation";
  success: boolean;
  errorMessage?: string;
  errorType?: string;
  requestPayload?: any;
  responseData?: any;
}

export function trackGraphQLOperation(params: TrackGraphQLOperationParams): void {
  const { methodName, methodType, success, errorMessage, errorType, requestPayload, responseData } = params;

  const properties = enrichEventProperties<GraphQLEventProperties>({
    method_name: methodName,
    method_type: methodType,
    success,
    ...(errorMessage && { error_message: errorMessage }),
    ...(errorType && { error_type: errorType }),
    ...(requestPayload && { request_payload: requestPayload }),
    ...(responseData && { response_data: responseData })
  });

  trackEvent(GRAPHQL_EVENTS.GRAPHQL_OPERATION_EXECUTED, properties);

  if (!success) {
    trackEvent(GRAPHQL_EVENTS.GRAPHQL_ERROR, properties);
  }
}

// React Hooks
export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const properties = enrichEventProperties({
      page_path: location.pathname
    });

    trackEvent(NAVIGATION_EVENTS.PAGE_VIEWED, properties);
  }, [location]);
}
